import React from "react";
import { TranscodeStatus } from "../../api/upload.api";
import {
  CompatibilityStatus,
  VideoProbeResult,
  formatAspectRatio,
} from "../../utils/videoProbe";
import { formatDuration } from "shared/utils/formatDuration";
import "./VrMediaVerification.css";

export type ServerProbeState = "idle" | "sent" | "skipped";

const STATUS_LABEL: Record<CompatibilityStatus, string> = {
  compatible: "Compatible",
  warning: "Compatible with warnings",
  incompatible: "Not compatible",
};

type VrMediaVerificationProps = {
  title: string;
  probing: boolean;
  result: VideoProbeResult | null;
  format?: string;
  serverProbe?: ServerProbeState;
};

export const VrMediaVerification: React.FC<VrMediaVerificationProps> = ({
  title,
  probing,
  result,
  format,
  serverProbe = "idle",
}) => {
  if (!probing && !result) return null;

  const status = result?.validation.status;
  const m = result?.metadata;
  const hasDims = !!m && m.width > 0 && m.height > 0;

  return (
    <div
      className={`vr-verify-card ${status ?? ""}`}
      data-testid="vr-media-verification"
      aria-live="polite"
    >
      <div className="vr-verify-header">
        <span>VR Media Verification · {title}</span>
        <span className={`vr-verify-badge ${status ?? "pending"}`}>
          {probing || !status ? "Checking…" : STATUS_LABEL[status]}
        </span>
      </div>

      {m && (
        <dl className="vr-verify-grid">
          <dt>Resolution</dt>
          <dd>{hasDims ? `${m.width} × ${m.height}` : "Unknown"}</dd>
          <dt>Aspect ratio</dt>
          <dd>{hasDims ? formatAspectRatio(m.aspectRatio) : "Unknown"}</dd>
          <dt>Duration</dt>
          <dd>
            {m.durationSeconds > 0 ? formatDuration(m.durationSeconds) : "Unknown"}
          </dd>
          <dt>File size</dt>
          <dd>
            {m.fileSizeMB >= 1024
              ? `${(m.fileSizeMB / 1024).toFixed(2)} GB`
              : `${m.fileSizeMB.toFixed(2)} MB`}
          </dd>
          <dt>Format</dt>
          <dd>
            {m.fileExtension || "?"}
            {format ? ` · VR ${format}` : " · select a VR format"}
          </dd>
        </dl>
      )}

      {result && result.validation.errors.length > 0 && (
        <ul className="vr-verify-messages error">
          {result.validation.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      {result && result.validation.warnings.length > 0 && (
        <ul className="vr-verify-messages warning">
          {result.validation.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {serverProbe === "sent" && (
        <p className="vr-verify-note">Uploaded video verified by the server.</p>
      )}
      {serverProbe === "skipped" && (
        <p className="vr-verify-note">
          Server verification could not confirm the uploaded video. Check the
          file or retry verification during review.
        </p>
      )}
    </div>
  );
};

type TranscodeStatusViewProps = {
  status: TranscodeStatus | null;
  unavailable?: boolean;
};

export const TranscodeStatusView: React.FC<TranscodeStatusViewProps> = ({
  status,
  unavailable = false,
}) => {
  if (unavailable) {
    return (
      <div className="transcode-status" data-testid="transcode-status">
        Transcoding status is currently unavailable.
      </div>
    );
  }
  if (!status) return null;

  let label: string;
  switch (status.state) {
    case "queued":
      label = "Transcoding: queued";
      break;
    case "processing":
      label =
        status.progress !== null
          ? `Transcoding: ${status.rawStatus || "processing"} (${status.progress}%)`
          : `Transcoding: ${status.rawStatus || "processing"}…`;
      break;
    case "completed":
      label = "Transcoding complete";
      break;
    case "failed":
      label = `Transcoding failed${status.message ? `: ${status.message}` : ""}`;
      break;
    default:
      label = status.rawStatus ? `Transcoding: ${status.rawStatus}` : "";
  }
  if (!label) return null;

  return (
    <div
      className={`transcode-status ${status.state}`}
      data-testid="transcode-status"
      aria-live="polite"
    >
      {label}
      {status.state === "processing" && status.progress !== null && (
        <div className="transcode-status-bar">
          <span style={{ width: `${status.progress}%` }} />
        </div>
      )}
    </div>
  );
};
